import { useMemo, useState } from 'react';
import { api } from '../api.ts';
import { useSession } from '../session.tsx';
import { getActiveWorkspace } from '../api.ts';
import { Card, ErrorState, Field, Loading, useAsync, useDebounced } from '../ui.tsx';
import { BarChart, FunnelChart, LineChart, RetentionChart } from '../charts.tsx';
import { EVENT_LABEL, fmtDate, fmtDateTime, fmtDuration, fmtMoney, fmtNumber, fmtPercent } from '../format.ts';

interface Overview {
  views: number; unique_viewers: number; plays: number; play_rate: number;
  avg_watch_sec: number; total_watch_sec: number; completion_rate: number; completions: number;
  cta_shown: number; cta_clicked: number; ctr: number; leads: number; lead_rate: number;
  checkouts: number; purchases: number; conversion_rate: number; revenue: number; aov: number;
  spend: number; cpa: number | null; roas: number | null; roi: number | null;
  retention: { percent: number; sessions: number; rate: number }[];
}

const RANGES = [
  { value: '7', label: 'Últimos 7 dias' },
  { value: '30', label: 'Últimos 30 dias' },
  { value: '90', label: 'Últimos 90 dias' },
  { value: '0', label: 'Todo o período' },
];

const DIMENSIONS = [
  { id: 'campaign', label: 'Campanha' },
  { id: 'adset', label: 'Conjunto' },
  { id: 'ad', label: 'Anúncio' },
  { id: 'source', label: 'Origem' },
  { id: 'country', label: 'País' },
  { id: 'device', label: 'Dispositivo' },
  { id: 'video', label: 'Vídeo' },
];

export function AnalyticsPage(): JSX.Element {
  const { workspace } = useSession();
  const initialVideo = new URLSearchParams(window.location.search).get('video') ?? '';

  const [range, setRange] = useState('30');
  const [videoId, setVideoId] = useState(initialVideo);
  const [device, setDevice] = useState('');
  const [country, setCountry] = useState('');
  const [campaign, setCampaign] = useState('');
  const [spendInput, setSpendInput] = useState('');
  const [dimension, setDimension] = useState('campaign');
  const [eventName, setEventName] = useState('');
  const [eventSearch, setEventSearch] = useState('');

  const spend = useDebounced(spendInput, 500);
  const search = useDebounced(eventSearch, 400);

  const from = useMemo(() => {
    if (range === '0') return '';
    const days = Number(range);
    return new Date(Date.now() - days * 86_400_000).toISOString();
  }, [range]);

  const params = useMemo(() => {
    const query = new URLSearchParams();
    if (from) query.set('from', from);
    if (videoId) query.set('video', videoId);
    if (device) query.set('device', device);
    if (country) query.set('country', country);
    if (campaign) query.set('campaign', campaign);
    if (spend) query.set('spend', spend);
    return query;
  }, [from, videoId, device, country, campaign, spend]);

  const videos = useAsync(() => api<{ videos: { id: string; title: string }[] }>('/api/videos?limit=200'), [workspace?.id]);
  const overview = useAsync(
    () => api<{ overview: Overview; funnel: { name: string; count: number; sessions: number }[]; dimensions: any }>(
      `/api/analytics/overview?${params.toString()}`,
    ),
    [workspace?.id, params.toString()],
  );
  const series = useAsync(
    () => api<{
      daily: { date: string; views: number; plays: number; clicks: number; leads: number; purchases: number; revenue: number }[];
      retention: { percent: number; rate: number; sessions: number }[];
      cta: { element_id: string; cta_name: string; shown: number; clicked: number; ctr: number; leads: number; checkouts: number; purchases: number; revenue: number }[];
    }>(`/api/analytics/series?${params.toString()}`),
    [workspace?.id, params.toString()],
  );
  const breakdown = useAsync(
    () => api<{ rows: any[] }>(`/api/analytics/breakdown?dimension=${dimension}&${params.toString()}`),
    [workspace?.id, dimension, params.toString()],
  );
  const events = useAsync(() => {
    const query = new URLSearchParams(params);
    if (eventName) query.set('name', eventName);
    if (search) query.set('search', search);
    query.set('limit', '80');
    return api<{ events: any[]; total: number }>(`/api/analytics/events?${query.toString()}`);
  }, [workspace?.id, params.toString(), eventName, search]);

  const csvHref = useMemo(() => {
    const query = new URLSearchParams(params);
    if (eventName) query.set('name', eventName);
    if (search) query.set('search', search);
    const ws = getActiveWorkspace();
    if (ws) query.set('workspace', ws);
    return `/api/analytics/events.csv?${query.toString()}`;
  }, [params, eventName, search]);

  const data = overview.data?.overview;
  const dims = overview.data?.dimensions;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Analytics</h1>
          <p className="page-sub">Retenção, funil, campanhas e receita — tudo a partir dos eventos do player.</p>
        </div>
        <a className="btn secondary" href={csvHref}>Exportar CSV</a>
      </div>

      <Card title="Filtros">
        <div className="grid cols-4">
          <Field label="Período" id="a-range">
            <select id="a-range" value={range} onChange={(e) => setRange(e.target.value)}>
              {RANGES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </Field>
          <Field label="Vídeo" id="a-video">
            <select id="a-video" value={videoId} onChange={(e) => setVideoId(e.target.value)}>
              <option value="">Todos os vídeos</option>
              {(videos.data?.videos ?? []).map((video) => <option key={video.id} value={video.id}>{video.title}</option>)}
            </select>
          </Field>
          <Field label="Dispositivo" id="a-device">
            <select id="a-device" value={device} onChange={(e) => setDevice(e.target.value)}>
              <option value="">Todos</option>
              {(dims?.devices ?? []).map((value: string) => <option key={value} value={value}>{value}</option>)}
            </select>
          </Field>
          <Field label="País" id="a-country">
            <select id="a-country" value={country} onChange={(e) => setCountry(e.target.value)}>
              <option value="">Todos</option>
              {(dims?.countries ?? []).map((value: string) => <option key={value} value={value}>{value}</option>)}
            </select>
          </Field>
          <Field label="Campanha (ID)" id="a-campaign">
            <select id="a-campaign" value={campaign} onChange={(e) => setCampaign(e.target.value)}>
              <option value="">Todas</option>
              {(dims?.campaigns ?? []).map((value: string) => <option key={value} value={value}>{value}</option>)}
            </select>
          </Field>
          <Field label="Investimento no período (R$)" id="a-spend" hint="Base para CPA, ROAS e ROI.">
            <input id="a-spend" type="number" min={0} step="0.01" value={spendInput} onChange={(e) => setSpendInput(e.target.value)} placeholder="0,00" />
          </Field>
        </div>
      </Card>

      <div style={{ height: 18 }} />

      {overview.loading && <Loading />}
      {overview.error && <ErrorState message={overview.error} onRetry={overview.reload} />}

      {data && (
        <>
          <div className="grid cols-4">
            <div className="stat"><p className="stat-label">Views</p><p className="stat-value">{fmtNumber(data.views)}</p><p className="stat-meta">{fmtNumber(data.unique_viewers)} espectadores únicos</p></div>
            <div className="stat"><p className="stat-label">Plays</p><p className="stat-value">{fmtNumber(data.plays)}</p><p className="stat-meta">taxa de play {fmtPercent(data.play_rate)}</p></div>
            <div className="stat"><p className="stat-label">Tempo médio assistido</p><p className="stat-value">{fmtDuration(data.avg_watch_sec)}</p><p className="stat-meta">total {fmtDuration(data.total_watch_sec)}</p></div>
            <div className="stat"><p className="stat-label">Conclusão</p><p className="stat-value">{fmtPercent(data.completion_rate)}</p><p className="stat-meta">{fmtNumber(data.completions)} sessões até o fim</p></div>

            <div className="stat"><p className="stat-label">CTAs exibidos</p><p className="stat-value">{fmtNumber(data.cta_shown)}</p></div>
            <div className="stat"><p className="stat-label">Cliques</p><p className="stat-value">{fmtNumber(data.cta_clicked)}</p><p className="stat-meta">CTR {fmtPercent(data.ctr, 2)}</p></div>
            <div className="stat"><p className="stat-label">Leads</p><p className="stat-value">{fmtNumber(data.leads)}</p><p className="stat-meta">{fmtPercent(data.lead_rate, 2)} dos plays</p></div>
            <div className="stat"><p className="stat-label">Checkouts</p><p className="stat-value">{fmtNumber(data.checkouts)}</p></div>

            <div className="stat"><p className="stat-label">Compras</p><p className="stat-value">{fmtNumber(data.purchases)}</p><p className="stat-meta">conversão {fmtPercent(data.conversion_rate, 2)}</p></div>
            <div className="stat"><p className="stat-label">Receita</p><p className="stat-value">{fmtMoney(data.revenue)}</p><p className="stat-meta">ticket médio {fmtMoney(data.aov)}</p></div>
            <div className="stat">
              <p className="stat-label">CPA</p>
              <p className="stat-value">{data.cpa === null ? '—' : fmtMoney(data.cpa)}</p>
              <p className="stat-meta">{data.spend ? `investimento ${fmtMoney(data.spend)}` : 'informe o investimento'}</p>
            </div>
            <div className="stat">
              <p className="stat-label">ROAS / ROI</p>
              <p className="stat-value">{data.roas === null ? '—' : `${data.roas.toFixed(2)}×`}</p>
              <p className="stat-meta">{data.roi === null ? 'informe o investimento' : `ROI ${fmtPercent(data.roi, 1)}`}</p>
            </div>
          </div>

          <div style={{ height: 18 }} />

          <div className="grid cols-2">
            <Card title="Curva de retenção" subtitle="Fração das sessões que ainda estava assistindo em cada ponto do vídeo.">
              <RetentionChart points={data.retention} />
            </Card>
            <Card title="Funil" subtitle="Sessões distintas em cada etapa.">
              <FunnelChart
                steps={[
                  { label: 'Impressões', value: data.views },
                  { label: 'Plays', value: data.plays },
                  { label: '50% assistido', value: data.retention.find((r) => r.percent === 50)?.sessions ?? 0 },
                  { label: 'CTA exibido', value: data.cta_shown },
                  { label: 'CTA clicado', value: data.cta_clicked },
                  { label: 'Checkout', value: data.checkouts },
                  { label: 'Compra', value: data.purchases },
                ]}
              />
            </Card>
          </div>

          <div style={{ height: 18 }} />

          <Card title="Evolução diária">
            {series.loading && <Loading />}
            {series.data && (
              <LineChart
                labels={series.data.daily.map((row) => fmtDate(row.date))}
                series={[
                  { key: 'views', label: 'Views', values: series.data.daily.map((row) => Number(row.views)) },
                  { key: 'plays', label: 'Plays', values: series.data.daily.map((row) => Number(row.plays)) },
                  { key: 'clicks', label: 'Cliques', values: series.data.daily.map((row) => Number(row.clicks)) },
                  { key: 'purchases', label: 'Compras', values: series.data.daily.map((row) => Number(row.purchases)) },
                ]}
              />
            )}
          </Card>

          <div style={{ height: 18 }} />

          <div className="grid cols-2">
            <Card title="Cliques por CTA" subtitle="Ordenado por volume de cliques no período.">
              {series.data && series.data.cta.length > 0 ? (
                <>
                  <BarChart
                    rows={[...series.data.cta]
                      .sort((a, b) => b.clicked - a.clicked)
                      .slice(0, 10)
                      .map((row) => ({
                        label: row.cta_name,
                        value: row.clicked,
                        meta: `CTR ${fmtPercent(row.ctr, 1)}`,
                      }))}
                    colorIndex={0}
                    emptyLabel="Nenhum clique registrado."
                  />
                  <div style={{ height: 16 }} />
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr><th>CTA</th><th className="num">Exibições</th><th className="num">Cliques</th><th className="num">CTR</th><th className="num">Compras</th></tr>
                      </thead>
                      <tbody>
                        {series.data.cta.map((row) => (
                          <tr key={row.element_id}>
                            <td>{row.cta_name}</td>
                            <td className="num">{fmtNumber(row.shown)}</td>
                            <td className="num">{fmtNumber(row.clicked)}</td>
                            <td className="num">{fmtPercent(row.ctr, 2)}</td>
                            <td className="num">{fmtNumber(row.purchases)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <p style={{ color: 'var(--text-3)', margin: 0 }}>Nenhum CTA exibido no período.</p>
              )}
            </Card>

            <Card
              title="Quebra por dimensão"
              actions={
                <select value={dimension} onChange={(e) => setDimension(e.target.value)} style={{ width: 180 }}>
                  {DIMENSIONS.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
                </select>
              }
            >
              {breakdown.loading && <Loading />}
              {breakdown.data && (
                <BarChart
                  rows={breakdown.data.rows.slice(0, 10).map((row) => ({
                    label: row.label,
                    value: Number(row.views),
                    meta: `${fmtNumber(row.purchases)} compras`,
                  }))}
                  colorIndex={1}
                  emptyLabel="Sem dados para essa dimensão."
                />
              )}
            </Card>
          </div>

          <div style={{ height: 18 }} />

          <Card
            title="Eventos detalhados"
            subtitle={events.data ? `${fmtNumber(events.data.total)} evento(s) no filtro atual` : undefined}
            actions={<a className="btn secondary small" href={csvHref}>Baixar CSV</a>}
          >
            <div className="grid cols-3">
              <Field label="Tipo de evento" id="e-name">
                <select id="e-name" value={eventName} onChange={(e) => setEventName(e.target.value)}>
                  <option value="">Todos</option>
                  {Object.entries(EVENT_LABEL).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                </select>
              </Field>
              <Field label="Buscar" id="e-search" hint="Sessão, event_id, URL ou nome do CTA.">
                <input id="e-search" value={eventSearch} onChange={(e) => setEventSearch(e.target.value)} />
              </Field>
            </div>

            {events.loading && <Loading />}
            {events.data && (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Quando</th><th>Evento</th><th>Sessão</th><th className="num">%</th>
                      <th>CTA</th><th>Dispositivo</th><th>Campanha</th><th className="num">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.data.events.map((event) => (
                      <tr key={event.event_id}>
                        <td style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(event.ts)}</td>
                        <td>{EVENT_LABEL[event.name] ?? event.name}</td>
                        <td><code style={{ fontSize: 11 }}>{String(event.session_id).slice(0, 14)}</code></td>
                        <td className="num">{Math.round(event.percent)}%</td>
                        <td>{event.cta_name ?? '—'}</td>
                        <td>{event.device} · {event.browser}</td>
                        <td>{event.attribution?.utm_campaign ?? event.attribution?.campaign_id ?? '—'}</td>
                        <td className="num">{event.value ? fmtMoney(event.value) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {events.data && events.data.events.length === 0 && (
              <p style={{ color: 'var(--text-3)' }}>Nenhum evento encontrado com esses filtros.</p>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

export function LeadsPage(): JSX.Element {
  const { workspace } = useSession();
  const [search, setSearch] = useState('');
  const debounced = useDebounced(search);
  const leads = useAsync(
    () => api<{ leads: any[]; total: number }>(`/api/analytics/leads?limit=200&search=${encodeURIComponent(debounced)}`),
    [workspace?.id, debounced],
  );
  const csvHref = `/api/analytics/leads.csv?workspace=${getActiveWorkspace() ?? ''}`;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Leads</h1>
          <p className="page-sub">Cadastros enviados pelos formulários dentro dos vídeos, com atribuição preservada.</p>
        </div>
        <a className="btn secondary" href={csvHref}>Exportar CSV</a>
      </div>

      <Card title="Buscar">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nome, e-mail ou telefone" />
      </Card>

      <div style={{ height: 18 }} />

      {leads.loading && <Loading />}
      {leads.error && <ErrorState message={leads.error} onRetry={leads.reload} />}
      {leads.data && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Data</th><th>Nome</th><th>E-mail</th><th>Telefone</th><th>Origem</th><th>Campanha</th></tr>
            </thead>
            <tbody>
              {leads.data.leads.map((lead) => (
                <tr key={lead.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(lead.created_at)}</td>
                  <td>{lead.name || '—'}</td>
                  <td>{lead.email || '—'}</td>
                  <td>{lead.phone || '—'}</td>
                  <td>{lead.attribution?.utm_source ?? '—'}</td>
                  <td>{lead.attribution?.utm_campaign ?? lead.attribution?.campaign_id ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {leads.data.leads.length === 0 && <p style={{ padding: 18, color: 'var(--text-3)', margin: 0 }}>Nenhum lead ainda.</p>}
        </div>
      )}
    </div>
  );
}
